import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { haveListApi } from '../collectionList/haveList/haveListApi';

/**
 * Request types for universal bulk import
 */
export interface UniversalCardImportItem {
  scryfallUuid: string;
  cardName?: string;
  setCode?: string;
  quantity?: number;
  foilQuantity?: number;
  condition?: string;
  language?: string;
}

/**
 * Import session types (for TradeBinder POST session flow)
 */
export interface ImportSessionCard {
  scryfallUuid: string;
  cardName: string;
  setCode?: string;
  quantity: number;
  foilQuantity: number;
  language?: string;
}

export interface ImportSessionResponse {
  sessionId: string;
  source: string;
  totalCards: number;
  skippedRows?: number;
  parseWarnings?: string[];
  page: number;
  totalPages: number;
  cards: ImportSessionCard[];
  createdAt: string;
  expiresAt: string;
}

export interface ImportFromSessionRequest {
  sessionId: string;
  condition: string;
}

export interface UniversalBulkImportRequest {
  cards: UniversalCardImportItem[];
  source?: string;
}

/**
 * Response types for universal bulk import
 */
export interface UniversalBulkImportError {
  row: number;
  scryfallUuid?: string;
  cardName?: string;
  error: string;
}

export interface UniversalImportedCard {
  id: number;
  cardName: string;
  quantity: number;
  foil: boolean;
  condition: string;
}

export interface UniversalBulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: UniversalBulkImportError[];
  importedCards: UniversalImportedCard[];
  importId: string;
}

export const importApi = createApi({
  reducerPath: 'importApi',
  baseQuery: baseQuery,
  tagTypes: ['Import'],
  endpoints: (builder) => ({
    /**
     * Fetch import session data.
     * Used when TradeBinder redirects with ?session=xxx.
     * Public endpoint - no auth required.
     */
    getImportSession: builder.query<
      ImportSessionResponse,
      { sessionId: string; page?: number }
    >({
      query: ({ sessionId, page = 1 }) => ({
        url: `/import-session/${sessionId}`,
        params: { page, limit: 100 },
      }),
    }),

    /**
     * Import directly from a session.
     * Backend reads all cards from stored session and performs bulk import.
     * More efficient for large collections (no need to fetch all pages to frontend).
     */
    importFromSession: builder.mutation<UniversalBulkImportResponse, ImportFromSessionRequest>({
      query: ({ sessionId, condition }) => ({
        url: `/import-session/${sessionId}/import`,
        method: 'POST',
        body: { condition },
        requiresAuth: true,
        timeout: 300000, // 5 min timeout for large collections
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(haveListApi.util.invalidateTags(['HaveList']));
        } catch {
          // Import failed, no cache invalidation needed
        }
      },
    }),

    /**
     * Universal bulk import endpoint.
     * Accepts Scryfall UUIDs for direct card matching (100% match rate for TradeBinder).
     * Creates separate entries for foil and non-foil quantities.
     */
    bulkImportUniversal: builder.mutation<UniversalBulkImportResponse, UniversalBulkImportRequest>({
      query: (body) => ({
        url: '/have-list/bulk-import-universal',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 120000, // 2 min timeout (backend processes in batches of 100)
      }),
      // Invalidate HaveList cache on successful import
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate HaveList cache to refresh collection view
          dispatch(haveListApi.util.invalidateTags(['HaveList']));
        } catch {
          // Import failed, no cache invalidation needed
        }
      },
    }),
  }),
});

export const {
  useLazyGetImportSessionQuery,
  useImportFromSessionMutation,
  useBulkImportUniversalMutation,
} = importApi;
